import { useCallback, useState } from "react";
import { getGoogleDrivePickerAccessToken } from "../../../../services/socialTokenService";
import { startGoogleDriveOAuthFlow } from "./googleDriveOAuth";
import { openGoogleDrivePicker } from "./googleDrivePicker";
import { downloadDriveFileAsFile } from "./googleDriveDownload";
import {
  isGoogleDriveAttachmentsConfigured,
  getRememberedGoogleDriveProfileId,
} from "./googleDriveEnv";

/**
 * Try to fetch a picker access token; if the backend has no stored Drive connection
 * (404), kick off OAuth. Otherwise open the Picker and download the picked files
 * client-side. Downstream (useAttachmentMedia) treats them like locally-picked files.
 */
export function useGoogleDriveChatImport(options: {
  customerId: string | undefined;
  idToken: string;
}) {
  const [googleDriveBusy, setGoogleDriveBusy] = useState(false);
  // Narrower than googleDriveBusy: true only for the token-fetch + Picker-script-load
  // gap before Google's own Picker UI takes over. googleDriveBusy stays true for the
  // whole time the Picker is open (through pick/cancel), which would keep a "loading"
  // overlay covering the native Picker if it drove the overlay too.
  const [googleDrivePickerOpening, setGoogleDrivePickerOpening] = useState(false);
  // True while picked files are being fetched from Drive and hydrated into browser
  // Files (downloadDriveFileAsFile). The Picker is already closed by this point, so
  // without this the composer just sits idle for a beat before attachments appear.
  const [googleDriveDownloading, setGoogleDriveDownloading] = useState(false);
  // Read fresh on every render (not memoized) so it reflects localStorage right
  // after the OAuth callback writes it, without needing a manual refresh trigger.
  const googleDriveConnected = Boolean(
    getRememberedGoogleDriveProfileId(options.customerId)
  );

  const importFromGoogleDrive = useCallback(async (): Promise<File[]> => {
    if (!isGoogleDriveAttachmentsConfigured()) {
      throw new Error(
        "Google Drive attachments are not configured for this environment."
      );
    }
    if (!options.customerId || !options.idToken) {
      throw new Error(
        "Select a business profile and sign in to use Google Drive."
      );
    }

    const developerKey = process.env.REACT_APP_GOOGLE_API_KEY?.trim();
    if (!developerKey) {
      throw new Error("REACT_APP_GOOGLE_API_KEY is required for Google Picker");
    }

    const startOAuth = () => {
      const returnTo = `${window.location.pathname}${window.location.search}`;
      startGoogleDriveOAuthFlow({
        customerId: options.customerId as string,
        returnTo,
        // Chat is the flow that wants the Picker to open by itself on return.
        autoOpenPickerOnReturn: true,
      });
    };

    setGoogleDriveBusy(true);
    setGoogleDrivePickerOpening(true);
    try {
      const rememberedProfileId = getRememberedGoogleDriveProfileId(
        options.customerId
      );
      // No known connection on this device → connect first. The OAuth callback
      // persists the profile id, which the access-token lookup then targets by
      // exact secret name (the backend role can't list secrets to find it blind).
      if (!rememberedProfileId) {
        startOAuth();
        return [];
      }

      const tokenResult = await getGoogleDrivePickerAccessToken(
        options.customerId,
        options.idToken,
        rememberedProfileId
      );
      if (tokenResult === null) {
        // Stored connection is gone/expired on the backend → reconnect.
        startOAuth();
        return [];
      }

      const { access_token } = tokenResult;

      const refs = await openGoogleDrivePicker({
        accessToken: access_token,
        developerKey,
        onBeforeShow: () => setGoogleDrivePickerOpening(false),
      });
      if (!refs.length) {
        return [];
      }

      setGoogleDriveDownloading(true);
      try {
        return await Promise.all(
          refs.map((ref) => downloadDriveFileAsFile(ref, access_token))
        );
      } finally {
        setGoogleDriveDownloading(false);
      }
    } finally {
      setGoogleDriveBusy(false);
      setGoogleDrivePickerOpening(false);
      setGoogleDriveDownloading(false);
    }
  }, [options.customerId, options.idToken]);

  return {
    importFromGoogleDrive,
    googleDriveBusy,
    googleDrivePickerOpening,
    googleDriveDownloading,
    googleDriveConnected,
  };
}
